"use client";
import { jsx } from "react/jsx-runtime";
import { createContext, useContext } from "react";
import { CREHANA_BRANDING } from "@crehana/constants/branding";
import { useLibraryCrehanaAuthGetCentralizedDataQuery } from "./get-centralized-data.v5.generated.js";
import { getSlugFromUrl } from "./utils/get-slug-from-url.js";
import { transformCentralizedOrgData } from "./utils/transform-centralized-org-data.js";
import { transformCurrentSessionSecurity } from "./utils/transform-current-session-security.js";
const Context = /*#__PURE__*/ createContext(void 0);
const AuthCentralizedProvider = ({ children, organizationSlug: organizationSlugFromProps })=>{
    const organizationSlugFromUrl = getSlugFromUrl();
    const organizationSlug = organizationSlugFromProps || organizationSlugFromUrl || '';
    const { data, loading, error, refetch } = useLibraryCrehanaAuthGetCentralizedDataQuery({
        variables: {
            organization_slug: organizationSlug || ''
        },
        context: {
            clientName: 'v5'
        }
    });
    const getSessionData = ()=>{
        if (!data || !data.security) return;
        const centralizedSession = data.security.current_session?.centralized_session;
        const centralizedUser = centralizedSession?.centralized_user_organization;
        const currentPageCentralizedOrganization = transformCentralizedOrgData(data.get_centralized_organization);
        if (loading || error || !centralizedSession || !centralizedUser) return {
            me: void 0,
            currentPageOrganization: currentPageCentralizedOrganization
        };
        const currentPageOrganization = currentPageCentralizedOrganization ? currentPageCentralizedOrganization : centralizedUser.organization ? {
            id: Number(centralizedUser.organization.id),
            name: centralizedUser.organization.name,
            slug: centralizedUser.organization.slug,
            branding: CREHANA_BRANDING
        } : void 0;
        const security = transformCurrentSessionSecurity(data.security, data.security.talent_current_session);
        const isSuperAdminUser = security.applicationRoles.includes('ROLE_SUPERADMIN');
        const centralizedAdminOrganizationSlugs = centralizedSession?.centralized_admin_organization_slugs;
        const canAccessCurrentOrganization = isSuperAdminUser && currentPageOrganization && centralizedAdminOrganizationSlugs ? centralizedAdminOrganizationSlugs.includes(currentPageOrganization.slug) : organizationSlug === centralizedUser.organization.slug;
        const sessionData = {
            me: {
                id: Number(centralizedUser.user.id),
                helpers: {
                    isAdminOrSubAdmin: security.applicationRoles.includes('ROLE_ADMIN') || security.applicationRoles.includes('ROLE_SUPERADMIN')
                },
                organization: {
                    id: Number(centralizedUser.organization.id),
                    slug: centralizedUser.organization.slug,
                    name: centralizedUser.organization.name
                },
                canAccessOrganizations: centralizedAdminOrganizationSlugs || void 0,
                canAccessCurrentOrganization: canAccessCurrentOrganization,
                user: {
                    id: centralizedUser.user.id,
                    email: centralizedUser.user.email,
                    firstname: centralizedUser.user.first_name,
                    fullName: centralizedUser.user.full_name,
                    lastname: centralizedUser.user.last_name,
                    avatarUrl: centralizedUser.avatar_url || void 0
                },
                status: centralizedUser.status,
                area: centralizedUser.area ? {
                    id: centralizedUser.area.id,
                    label: centralizedUser.area.label,
                    name: centralizedUser.area.name,
                    createdAt: centralizedUser.area.created_at
                } : void 0,
                headquarter: centralizedUser.headquarter ? {
                    id: centralizedUser.headquarter.id,
                    name: centralizedUser.headquarter.name
                } : void 0,
                position: centralizedUser.position ? {
                    id: centralizedUser.position.id,
                    name: centralizedUser.position.name
                } : void 0,
                positionCategory: centralizedUser.position_category ? {
                    id: centralizedUser.position_category.id,
                    name: centralizedUser.position_category.name
                } : void 0,
                security
            },
            currentPageOrganization
        };
        if (security.applicationRoles.includes('ROLE_STAFF') && currentPageOrganization) {
            sessionData.me.organization = currentPageOrganization;
            sessionData.me.canAccessCurrentOrganization = true;
        }
        return sessionData;
    };
    const sessionData = getSessionData();
    return /*#__PURE__*/ jsx(Context.Provider, {
        value: {
            me: sessionData?.me,
            currentPageOrganization: sessionData?.currentPageOrganization,
            loading: loading,
            error,
            refetch
        },
        children: children
    });
};
const useCentralizedProviderData = ()=>{
    const context = useContext(Context);
    if (!context) throw new Error('useCentralizedMe must be used within a AuthCentralizedProvider');
    return context;
};
const useCentralizedMe = ()=>{
    const { me, currentPageOrganization, loading, error } = useCentralizedProviderData();
    if (!me) throw new Error('The user must be logged in to use useCentralizedMe. Make sure you are waiting for the query to finish loading before calling this hook.');
    if (!currentPageOrganization) throw new Error("The current organization slug, from the url, doesn't exists. Make sure the url is correct.");
    return {
        me,
        currentPageOrganization,
        loading,
        error
    };
};
export { AuthCentralizedProvider, useCentralizedMe, useCentralizedProviderData };
